import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Search, ShoppingBag, User, Menu, X, Phone, Mail } from 'lucide-react';
import { useCart } from '../context/CartContext';
import { Input } from './ui/input';
import { Button } from './ui/button';
import useSWR from 'swr';
import { publicSettings, subscribeToAuth } from '../lib/api';

const Header = () => {
  const { count, setIsOpen } = useCart();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [searchQ, setSearchQ] = useState('');
  const [user, setUser] = useState(null);
  const navigate = useNavigate();
  const { data: settings } = useSWR('publicSettings', publicSettings);

  useEffect(() => {
    const unsubscribe = subscribeToAuth((u) => {
      setUser(u);
    });
    return unsubscribe;
  }, []);

  const navLinks = [
    { name: 'Home', href: '/' },
    { name: 'Shop', href: '/shop' },
    { name: 'Brands', href: '/shop?filter=brands' },
    { name: 'For Him', href: '/shop?gender=men' },
    { name: 'For Her', href: '/shop?gender=women' },
    { name: 'Reviews', href: '/reviews' },
    { name: 'About', href: '/about' },
    { name: 'Contact', href: '/contact' },
  ];

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQ.trim()) navigate(`/shop?q=${encodeURIComponent(searchQ.trim())}`);
  };

  return (
    <header className="w-full sticky top-0 z-50">
      {/* Top bar */}
      <div className="bg-[#1a1a1a] text-white text-xs">
        <div className="max-w-7xl mx-auto px-4 py-2 flex items-center justify-between">
          <div className="hidden md:flex items-center gap-5">
            <span className="flex items-center gap-1.5"><Phone size={12}/> {settings?.contactPhone || '+91 98765 43210'}</span>
            <span className="flex items-center gap-1.5"><Mail size={12}/> {settings?.contactEmail || 'support@watchtrader.in'}</span>
          </div>
          <div className="flex-1 md:flex-none text-center md:text-right tracking-wide">
            Free Delivery On Prepaid Orders · Cash On Delivery Available Across India
          </div>
        </div>
      </div>

      {/* Main header */}
      <div className="bg-white border-b border-neutral-200">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between gap-6">
          <Link to="/" className="flex items-center gap-2">
            {settings?.logo_image ? (
              <img 
                src={settings.logo_image} 
                alt={settings.siteName || "Watch Trader"} 
                style={{ maxWidth: settings.logo_width ? `${settings.logo_width}px` : '200px' }} 
                className="object-contain max-h-24" 
              />
            ) : (
              <>
                <div className="w-10 h-10 rounded-full bg-[#b8892d] flex items-center justify-center text-white font-serif text-xl">W</div>
                <div className="leading-tight">
                  <div className="font-serif text-xl md:text-2xl tracking-wide text-[#1a1a1a]">Watch Trader</div>
                  <div className="text-[10px] tracking-[0.25em] text-neutral-500 uppercase">Premium Luxury Watches</div>
                </div>
              </>
            )}
          </Link>

          <form onSubmit={handleSearch} className="hidden md:flex flex-1 max-w-xl relative">
            <Input
              value={searchQ}
              onChange={(e) => setSearchQ(e.target.value)}
              placeholder="Search for Rolex, Omega, Tag Heuer..."
              className="pr-12 h-11 border-neutral-300 focus-visible:ring-[#b8892d]"
            />
            <button type="submit" className="absolute right-0 top-0 h-11 w-11 flex items-center justify-center bg-[#b8892d] text-white hover:bg-[#a07826] rounded-r-md transition-colors">
              <Search size={18}/>
            </button>
          </form>

          <div className="flex items-center gap-3">
            <Link to="/account" className="flex items-center gap-2 text-neutral-700 hover:text-[#b8892d] transition-colors">
              <User size={22}/>
              <span className="text-sm hidden md:block">{user ? 'Account' : 'Login'}</span>
            </Link>
            <button onClick={() => setIsOpen(true)} className="relative flex items-center gap-2 text-neutral-700 hover:text-[#b8892d] transition-colors">
              <ShoppingBag size={22}/>
              {count > 0 && (
                <span className="absolute -top-2 -right-2 bg-[#b8892d] text-white text-[10px] w-5 h-5 rounded-full flex items-center justify-center font-semibold">{count}</span>
              )}
            </button>
            <button onClick={() => setMobileOpen(!mobileOpen)} className="md:hidden text-neutral-700">
              {mobileOpen ? <X size={24}/> : <Menu size={24}/>}
            </button>
          </div>
        </div>

        {/* Nav */}
        <nav className="hidden md:block border-t border-neutral-100">
          <div className="max-w-7xl mx-auto px-4">
            <ul className="flex items-center justify-center gap-8 h-12">
              {navLinks.map(l => (
                <li key={l.name}>
                  <Link to={l.href} className="text-sm font-medium text-neutral-700 hover:text-[#b8892d] transition-colors tracking-wide uppercase">
                    {l.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </nav>

        {/* Mobile nav */}
        {mobileOpen && (
          <div className="md:hidden border-t border-neutral-200 bg-white">
            <div className="px-4 py-3">
              <form onSubmit={handleSearch} className="flex mb-3">
                <Input value={searchQ} onChange={(e) => setSearchQ(e.target.value)} placeholder="Search watches..." className="h-10"/>
                <Button type="submit" className="ml-2 bg-[#b8892d] hover:bg-[#a07826]"><Search size={16}/></Button>
              </form>
              <ul className="space-y-1">
                {navLinks.map(l => (
                  <li key={l.name}>
                    <Link to={l.href} onClick={() => setMobileOpen(false)} className="block py-2 text-neutral-800 hover:text-[#b8892d] font-medium">
                      {l.name}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;
